import { gql } from '@apollo/client';
import { Button, Link } from '@apollo/orbit';
import React, { useMemo } from 'react';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';

import { MaybeEmbeddedLoginLink } from 'src/app/embedHelpers/MaybeEmbedLoginLink';
import { useSchema } from 'src/app/graph/hooks/useSchema';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

export const PublishThisGraphButtonFragment = gql`
  fragment PublishThisGraphButtonFragment on User {
    id
    memberships {
      account {
        id
        roles {
          canCreateService
        }
      }
      user {
        id
      }
    }
  }
`;

export const PublishThisGraphButton = ({
  user,
}: {
  user: GraphQLTypes.PublishThisGraphButtonFragment | undefined;
}) => {
  const { activeSchema } = useSchema();
  const location = useLocation();
  // this checks for SSO folks who can't create graphs as well as everyone else in one org
  const hasOneOrgAndCannotCreateGraph = useMemo(
    () =>
      user?.memberships.length === 1 &&
      !user.memberships[0]?.account.roles?.canCreateService,
    [user],
  );

  const publishToNewGraphDisabled = hasOneOrgAndCannotCreateGraph;

  const publishButtonDisabled =
    !user || !activeSchema || publishToNewGraphDisabled;

  return (
    <Tooltip
      interactive
      disabled={!publishButtonDisabled}
      label={
        !user ? (
          <div>
            You need to{' '}
            <MaybeEmbeddedLoginLink
              className="text-link"
              userSegmentType="sandbox"
            >
              log in
            </MaybeEmbeddedLoginLink>{' '}
            to publish this graph.
          </div>
        ) : !activeSchema ? (
          <div>
            Hmm ... we couldn't fetch your introspection schema from your
            current Sandbox endpoint. Make sure you've typed your GraphQL
            endpoint correctly, and added any authentication headers you need
            from{' '}
            <Link
              to={{
                ...setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.connectionSettings,
                }),
                state: {
                  linkEndpointModalData: {
                    openingIntent: 'Set url',
                  },
                },
              }}
            >
              the connection settings.
            </Link>
          </div>
        ) : (
          <div>
            You do not have permission to publish to this organization. Request
            permission from your Org Admin
          </div>
        )
      }
    >
      <Button
        variant="primary"
        isDisabled={publishButtonDisabled}
        as={publishButtonDisabled ? undefined : ReactRouterLink}
        to={setOverlayVisibleLocation({
          location,
          overlayName: Config.modals.publishSandboxCreateNewGraph,
        })}
        data-analytics-category="Sandbox"
        data-analytics-label={`option:${Config.modals.publishSandboxCreateNewGraph}`}
        aria-label="Publish this graph"
        size="sm"
        onClick={() =>
          ampli.connectGraphFlowStarted({
            'API Type': 'GraphQL',
          })
        }
      >
        Publish
      </Button>
    </Tooltip>
  );
};
